import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';
import type { HexTileMapKey } from '@/game/hexmap';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import {
  buildingMaxHp,
  cityBonuses,
  type EntityId,
  fromMilli,
  isCity,
  unitMaxHp,
} from '@/game/sim';
import {
  BAR_FADE,
  barColorStops,
  barFlash,
  barVisibility,
  COMBAT_LINGER,
  type CombatMark,
  inCombat,
  trackCombat,
} from '@/game/view/hpBars';
import { tickEngagements } from '@/game/view/tickCache';
import { useFrameStore, useLiveReaders } from './app/simFrame';

const TILE_RADIUS = config.hexMap.tile.radius;
const GROUND_Y = config.hexMap.tile.thickness;
/** Bar heights above the ground: units, buildings, cities (taller keep). */
const UNIT_BAR_Y = GROUND_Y + 0.92;
const BUILDING_BAR_Y = GROUND_Y + 1.3;
const CITY_BAR_Y = GROUND_Y + 1.4;
/** Bar sizes (world units): a unit's, a building's; the backing adds a rim. */
const UNIT_BAR = { w: 0.62, h: 0.075 };
const BUILDING_BAR = { w: 1.1, h: 0.1 };
const RIM = 0.025;
/** Above this camera height bars only show for things in combat. */
const FAR_CAMERA_Y = 48;
/** Most bars drawn at once (each is two instances: backing and fill). */
const MAX_BARS = 400;

// The design system's status colours (index.css ok/warn; danger a shade
// stronger so a nearly dead bar reads at a glance), a dark glass backing and
// white for the hit flash.
const COLORS = {
  ok: new THREE.Color('#6ee7b7'),
  warn: new THREE.Color('#fcd34d'),
  danger: new THREE.Color('#f87171'),
};
const BACKING = new THREE.Color('#0c0e14');
const BACKING_HIT = new THREE.Color('#7f1d1d');
const FLASH = new THREE.Color('#ffffff');

const geometry = new THREE.PlaneGeometry(1, 1);
const material = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0.92,
  depthTest: false,
  depthWrite: false,
  toneMapped: false,
});

// Scratch objects reused every frame (no per-frame allocation).
const scratchMatrix = new THREE.Matrix4();
const scratchPos = new THREE.Vector3();
const scratchScale = new THREE.Vector3();
const scratchColor = new THREE.Color();
const scratchTarget = new THREE.Vector3();

function noRaycast() {}

interface BarEntry {
  id: EntityId;
  unit: boolean;
  /** Hp fraction at the last tick. */
  fraction: number;
  /** A building's bar position (units follow their smoothed position). */
  x: number;
  y: number;
  z: number;
}

/**
 * Thin hp bars over units and buildings that are damaged or in combat
 * (engaged, hit or shooting in the last COMBAT_LINGER s): the player's own
 * and rivals' in sight (attackers revealed by a hit included, their tile
 * being in sight). Hidden at full hp out of combat, fading out after it;
 * ok → warn → danger by fraction, flashing on a hit. One instanced mesh
 * facing the camera; zoomed far out only fights keep their bars.
 */
export function HealthBarsLayer() {
  const { getState, getTileVisibility } = useLiveReaders();
  const { playerId, ctx } = useFrameStore().get();
  const { rules } = ctx;
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const marks = useRef(new Map<EntityId, CombatMark>());
  const entries = useRef<BarEntry[]>([]);
  const lastTick = useRef(-1);
  // Smoothed unit positions, the same easing UnitsLayer gives its models;
  // kept only for bars drawn last frame, so a bar shown again snaps to its unit.
  const rendered = useRef(new Map<EntityId, THREE.Vector3>());
  const drawn = useRef(new Set<EntityId>());

  useFrame(({ camera, clock }, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const state = getState();
    const now = clock.elapsedTime;

    // Once a tick: who fights, and which bars could show.
    if (state.tick !== lastTick.current) {
      lastTick.current = state.tick;
      trackCombat(marks.current, state, tickEngagements(state, ctx), now);
      const seen = (key: HexTileMapKey) => getTileVisibility(key) === 'visible';
      const list = entries.current;
      list.length = 0;
      const recent = (id: EntityId) => {
        const mark = marks.current.get(id);
        return !!mark && now - mark.combat <= COMBAT_LINGER + BAR_FADE;
      };
      for (const u of Object.values(state.units)) {
        if (u.inside) continue;
        if (u.owner !== playerId && !seen(getHexTileMapKey(u.pos))) continue;
        const fraction = u.hp / Math.max(1e-6, unitMaxHp(rules, u));
        if (fraction >= 1 - 1e-6 && !recent(u.id)) continue;
        list.push({ id: u.id, unit: true, fraction, x: 0, y: UNIT_BAR_Y, z: 0 });
      }
      let bonuses: ReturnType<typeof cityBonuses> | undefined;
      for (const b of Object.values(state.buildings)) {
        if (b.owner !== playerId && !seen(getHexTileMapKey(b.pos))) continue;
        // A site's hp grows as it's built: only a fight shows its bar.
        const site = b.buildProgress < 1;
        if (site && !recent(b.id)) continue;
        bonuses ??= cityBonuses(state, rules);
        const max = buildingMaxHp(state, rules, b, bonuses);
        const fraction = Math.min(1, b.hp / Math.max(1e-6, max));
        if (!site && fraction >= 1 - 1e-6 && !recent(b.id)) continue;
        const w = axialToWorld(b.pos, TILE_RADIUS);
        list.push({
          id: b.id,
          unit: false,
          fraction,
          x: w.x,
          y: isCity(b, rules) ? CITY_BAR_Y : BUILDING_BAR_Y,
          z: w.z,
        });
      }
    }

    const far = camera.position.y > FAR_CAMERA_Y;
    let n = 0;
    const nowDrawn = drawn.current;
    nowDrawn.clear();
    for (const e of entries.current) {
      if (n >= MAX_BARS) break;
      const mark = marks.current.get(e.id);
      if (far && !inCombat(mark, now)) continue;
      const show = barVisibility(mark, e.fraction, now);
      if (show <= 0) continue;
      if (e.unit) {
        const u = state.units[e.id];
        if (!u) continue;
        let q = u.pos.q;
        let r = u.pos.r;
        if (u.path && u.path.length > 0) {
          const p = fromMilli(u.progress ?? 0);
          q += (u.path[0].q - q) * p;
          r += (u.path[0].r - r) * p;
        }
        const w = axialToWorld({ q, r }, TILE_RADIUS);
        scratchTarget.set(w.x, e.y, w.z);
        let pos = rendered.current.get(e.id);
        if (!pos) {
          pos = scratchTarget.clone();
          rendered.current.set(e.id, pos);
        } else {
          pos.lerp(scratchTarget, 1 - Math.exp(-12 * delta));
        }
        scratchPos.copy(pos);
        nowDrawn.add(e.id);
      } else {
        scratchPos.set(e.x, e.y, e.z);
      }
      const size = e.unit ? UNIT_BAR : BUILDING_BAR;
      const flash = barFlash(mark, now);
      // Fading shrinks the bar's height (one material, no per-bar opacity).
      const h = size.h * show;

      scratchScale.set(size.w + RIM * 2, h + RIM * 2 * show, 1);
      scratchMatrix.compose(scratchPos, camera.quaternion, scratchScale);
      mesh.setMatrixAt(n * 2, scratchMatrix);
      mesh.setColorAt(n * 2, flash ? BACKING_HIT : BACKING);

      const f = Math.min(1, Math.max(0, e.fraction));
      // Left-aligned fill: the camera never yaws, so screen-right is world +x.
      scratchPos.x += -size.w / 2 + (size.w * f) / 2;
      scratchScale.set(Math.max(1e-4, size.w * f), h, 1);
      scratchMatrix.compose(scratchPos, camera.quaternion, scratchScale);
      mesh.setMatrixAt(n * 2 + 1, scratchMatrix);
      const [from, to, t] = barColorStops(f);
      scratchColor.copy(COLORS[from]).lerp(COLORS[to], t);
      if (flash) scratchColor.lerp(FLASH, 0.6);
      mesh.setColorAt(n * 2 + 1, scratchColor);
      n++;
    }
    for (const id of rendered.current.keys()) {
      if (!nowDrawn.has(id)) rendered.current.delete(id);
    }
    mesh.count = n * 2;
    // Only the bars drawn go up to the GPU, not the whole MAX_BARS buffer.
    if (n > 0) {
      for (const attr of [mesh.instanceMatrix, mesh.instanceColor]) {
        if (!attr) continue;
        attr.clearUpdateRanges();
        attr.addUpdateRange(0, n * 2 * attr.itemSize);
        attr.needsUpdate = true;
      }
    }
  });

  return (
    <instancedMesh
      ref={(mesh) => {
        meshRef.current = mesh;
        if (!mesh) return;
        mesh.count = 0;
        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        // Colours from the start, so the material compiles with them.
        if (!mesh.instanceColor) {
          mesh.instanceColor = new THREE.InstancedBufferAttribute(
            new Float32Array(MAX_BARS * 2 * 3),
            3
          );
          mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
        }
      }}
      args={[geometry, material, MAX_BARS * 2]}
      frustumCulled={false}
      renderOrder={20}
      raycast={noRaycast}
    />
  );
}
